/**
 * CurriculumPreviewSection — vista de solo lectura del currículo para el
 * Admin Institucional. Muestra los indicadores importados agrupados por unidad,
 * filtrable por grado y materia. NO permite editar (la gestión está en Super Admin).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { BookOpen, Loader2, Search, RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const GRADES = [
  { value: "4", label: "4° Grado" },
  { value: "5", label: "5° Grado" },
  { value: "6", label: "6° Grado" },
  { value: "7", label: "7° Grado" },
  { value: "8", label: "8° Grado" },
  { value: "9", label: "9° Grado" },
  { value: "1-bach", label: "1° Bachillerato" },
  { value: "2-bach", label: "2° Bachillerato" },
];

const SUBJECTS = [
  { value: "comunicacion-lenguaje", label: "Comunicación y Lenguaje" },
  { value: "matematica-datos", label: "Matemática y Datos" },
  { value: "ciencia-tecnologia", label: "Ciencia y Tecnología" },
  { value: "ciudadania-valores", label: "Ciudadanía y Valores" },
];

const BLOOM_COLOR = {
  Recordar: "bg-slate-100 text-slate-700 border-slate-200",
  Comprender: "bg-blue-50 text-blue-700 border-blue-200",
  Aplicar: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Analizar: "bg-amber-50 text-amber-700 border-amber-200",
  Evaluar: "bg-orange-50 text-orange-700 border-orange-200",
  Crear: "bg-purple-50 text-purple-700 border-purple-200",
};

export default function CurriculumPreviewSection() {
  const [grade, setGrade] = useState("7");
  const [subject, setSubject] = useState("comunicacion-lenguaje");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/curriculum/${grade}/${subject}/all`,
        { withCredentials: true },
      );
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar el currículo");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [grade, subject]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const needle = q.trim().toLowerCase();
    return rows.filter((r) =>
      (r.indicator_code || "").toLowerCase().includes(needle) ||
      (r.indicator_text || "").toLowerCase().includes(needle) ||
      (r.unit || "").toLowerCase().includes(needle),
    );
  }, [rows, q]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const r of filtered) {
      const key = r.unit || "Sin unidad";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    return Array.from(map.entries());
  }, [filtered]);

  const bloomCounts = useMemo(() => {
    const counts = {};
    for (const r of filtered) {
      const b = r.bloom_level || "Sin nivel";
      counts[b] = (counts[b] || 0) + 1;
    }
    return counts;
  }, [filtered]);

  return (
    <div className="space-y-4" data-testid="curriculum-preview-section">
      <div className="flex flex-wrap items-end gap-3 justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[#419cca]" /> Currículo de la institución
          </h2>
          <p className="text-sm text-gray-500">
            Vista de solo lectura de los indicadores importados. La gestión del currículo la realiza el Super Admin.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="curriculum-refresh-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          <span className="ml-1">Actualizar</span>
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end bg-white rounded-2xl border border-gray-100 p-4">
        <div className="min-w-[180px]">
          <label className="text-xs text-gray-500 font-medium block mb-1">Grado</label>
          <Select value={grade} onValueChange={setGrade}>
            <SelectTrigger data-testid="curriculum-grade-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {GRADES.map((g) => (
                <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[220px]">
          <label className="text-xs text-gray-500 font-medium block mb-1">Materia</label>
          <Select value={subject} onValueChange={setSubject}>
            <SelectTrigger data-testid="curriculum-subject-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SUBJECTS.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs text-gray-500 font-medium block mb-1">Buscar</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Código, unidad o texto…"
              className="pl-9"
              data-testid="curriculum-search-input"
            />
          </div>
        </div>
      </div>

      {/* Stats + Bloom distribution */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Total indicadores</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{filtered.length}</p>
          <p className="text-xs text-gray-400 mt-1">
            {rows.length !== filtered.length && `de ${rows.length}`}
          </p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Unidades</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{grouped.length}</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4 col-span-2">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Distribución Bloom</p>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(bloomCounts).sort((a, b) => b[1] - a[1]).map(([lvl, n]) => (
              <span
                key={lvl}
                className={`px-2 py-0.5 rounded-full text-xs border ${BLOOM_COLOR[lvl] || "bg-gray-50 text-gray-600 border-gray-200"}`}
              >
                {lvl} · {n}
              </span>
            ))}
            {Object.keys(bloomCounts).length === 0 && <span className="text-xs text-gray-400">—</span>}
          </div>
        </div>
      </div>

      {/* Grouped by unit */}
      {loading && rows.length === 0 && (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando indicadores…
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
          <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="font-bold text-gray-700 mb-1">Sin indicadores</h3>
          <p className="text-sm text-gray-500">
            {q ? "No hay coincidencias con tu búsqueda." : "Esta combinación de grado y materia aún no tiene currículo cargado."}
          </p>
        </div>
      )}

      {grouped.map(([unit, items]) => (
        <div key={unit} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-4 py-3 bg-gradient-to-r from-[#419cca]/5 to-[#8b5cf6]/5 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-bold text-sm text-gray-900">{unit}</h3>
            <span className="text-xs text-gray-500">{items.length} indicador{items.length !== 1 ? "es" : ""}</span>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">Código</TableHead>
                <TableHead>Indicador</TableHead>
                <TableHead className="w-32">Bloom</TableHead>
                <TableHead className="w-32 hidden md:table-cell">Dificultad</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((r) => (
                <TableRow key={r.id || `${unit}-${r.indicator_code}`} data-testid={`curriculum-row-${r.indicator_code}`}>
                  <TableCell className="font-mono text-xs text-gray-500">{r.indicator_code}</TableCell>
                  <TableCell className="text-sm text-gray-800">{r.indicator_text}</TableCell>
                  <TableCell>
                    {r.bloom_level ? (
                      <Badge variant="outline" className={`text-xs ${BLOOM_COLOR[r.bloom_level] || ""}`}>
                        {r.bloom_level}
                      </Badge>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-xs text-gray-500">
                    {r.difficulty_level || "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ))}
    </div>
  );
}
